/**
 * Vista de lista del tablero — la hermana obligatoria de todo tablero (Board
 * Pattern). Misma rejilla de tabla que Prospectos, sin fila seleccionada: al
 * hacer clic se abre el panel, no es maestro-detalle.
 */
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../ui/Checkbox.js';
import { BARRA_PENDIENTE } from '../ui/ListItem.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Pill } from '../PillGroup.js';
import { formatMoney } from '../../lib/money.js';
import type { Opportunity, FunnelStageWithTotals } from '../../hooks/useFunnels.js';

const COLS = 'var(--evo-table-check-col) minmax(0, 2fr) minmax(0, 1.2fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr)';

const HEAD_CELL = {
  padding: 'var(--evo-table-head-py) var(--evo-table-cell-px)',
  display: 'flex',
  alignItems: 'center',
  color: 'var(--evo-fg-3)',
  fontSize: 'var(--evo-list-header-fs)',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: 'var(--evo-ls-caps)',
  whiteSpace: 'nowrap' as const,
};

const CELL = {
  padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)',
  fontSize: 'var(--evo-list-subtitle-fs)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

interface Props {
  opportunities: Opportunity[];
  stages: FunnelStageWithTotals[];
  selected: Set<number>;
  onToggleSelect: (id: number) => void;
  /** Marca o desmarca TODO lo que se está viendo (no lo filtrado afuera). */
  onToggleAll: () => void;
  /** Llegó desde la última visita al módulo: barra cian a la izquierda. */
  esNueva: (o: Opportunity) => boolean;
  onOpen: (id: number) => void;
}

export function OpportunityList({ opportunities, stages, selected, onToggleSelect, onToggleAll, esNueva, onOpen }: Props) {
  const { t } = useTranslation();
  const stageById = new Map(stages.map(s => [s.id, s]));
  const todasMarcadas = opportunities.length > 0 && opportunities.every(o => selected.has(o.id));

  // De la más nueva a la más vieja. En el tablero el orden lo decide quien
  // arrastra; acá no hay arrastre, y lo último que entró es lo que se viene a
  // buscar. `id` desempata: `created_at` tiene resolución de un segundo y la
  // ingesta crea decenas de cartas dentro del mismo.
  const filas = [...opportunities].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt) || b.id - a.id);

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div
        style={{
          display: 'grid', gridTemplateColumns: COLS,
          height: 'var(--evo-toolbar-height)',
          alignItems: 'center',
          borderBottom: '1px solid var(--evo-stroke)',
          flexShrink: 0,
          background: 'var(--evo-bg-2)',
        }}
      >
        {/* El padding izquierdo va SOLO en la celda del cotejo; la primera
            celda de datos arranca en 0 (error del pasado: paddings apilados). */}
        <div style={{ padding: 'var(--evo-table-head-py) var(--evo-list-checkbox-gap) var(--evo-table-head-py) var(--evo-list-pad-x)' }}>
          <Checkbox
            checked={todasMarcadas}
            indeterminate={selected.size > 0 && !todasMarcadas}
            onChange={onToggleAll}
          />
        </div>
        <div style={{ ...HEAD_CELL, padding: 'var(--evo-table-head-py) var(--evo-table-cell-px) var(--evo-table-head-py) 0' }}>
          {t('opportunities.list.colOpportunity')}
        </div>
        <div style={HEAD_CELL}>{t('opportunities.list.colStage')}</div>
        <div style={HEAD_CELL}>{t('opportunities.list.colContact')}</div>
        <div style={HEAD_CELL}>{t('opportunities.list.colSource')}</div>
        <div style={{ ...HEAD_CELL, justifyContent: 'flex-end', padding: 'var(--evo-table-head-py) var(--evo-list-pad-x) var(--evo-table-head-py) var(--evo-table-cell-px)' }}>
          {t('opportunities.list.colValue')}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {filas.length === 0 && <EmptyState title={t('funnels.column.drop')} />}
        {filas.map(row => {
          const stage = stageById.get(row.stageId);
          return (
            <div
              key={row.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(row.id)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onOpen(row.id); } }}
              style={{
                display: 'grid', gridTemplateColumns: COLS,
                alignItems: 'stretch',
                minHeight: 'var(--evo-list-row-min-height)',
                cursor: 'pointer',
                borderBottom: '1px solid var(--evo-stroke)',
                boxShadow: esNueva(row) ? BARRA_PENDIENTE : 'none',
              }}
            >
              <div
                onClick={e => { e.stopPropagation(); onToggleSelect(row.id); }}
                style={{
                  display: 'flex', alignItems: 'flex-start',
                  padding: 'calc(var(--evo-table-cell-py) + var(--evo-list-check-nudge)) var(--evo-list-checkbox-gap) var(--evo-table-cell-py) var(--evo-list-pad-x)',
                }}
              >
                <Checkbox checked={selected.has(row.id)} onChange={() => onToggleSelect(row.id)} />
              </div>

              <div style={{ padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px) var(--evo-table-cell-py) 0', overflow: 'hidden' }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--evo-fg-1)', fontSize: 'var(--evo-list-title-fs)' }}>
                  {row.title}
                </div>
                {row.formName != null && (
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px', color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-subtitle-fs)' }}>
                    {row.formName}
                  </div>
                )}
              </div>

              <div style={{ ...CELL, display: 'flex', alignItems: 'flex-start' }}>
                {stage && (
                  <Pill variant="outline" size="xs" tone="muted">{stage.name}</Pill>
                )}
              </div>

              <div style={{ ...CELL, color: row.contactName ? 'var(--evo-fg-2)' : 'var(--evo-fg-4)' }}>
                {row.contactName ?? t('opportunities.noContact')}
              </div>

              <div style={{ ...CELL, color: 'var(--evo-fg-3)' }}>
                {t(`opportunities.source.${row.source}`)}
              </div>

              <div
                style={{
                  padding: 'var(--evo-table-cell-py) var(--evo-list-pad-x) var(--evo-table-cell-py) var(--evo-table-cell-px)',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
                }}
              >
                <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)', fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(row.valueCents, row.currency)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
